import React, { useEffect, useState } from "react";
import { EmptyState } from "../../../../components/recharts/ReMixCharts/components/EmptyState";
import { SheetTableSkeleton } from "./SheetTableSkeleton";

interface DaywiseRow {
  time_block: number;
  time_slot: string;
  values: (number | null)[];
}

interface DaywiseData {
  sheet: string;
  dates: string[];
  data: DaywiseRow[];
}

interface Props {
  date: string;
  fetchFn: (p: { date: string }) => Promise<DaywiseData>;
  onDataLoaded?: (data: DaywiseData) => void;
}

const TEAL = "#34656D";
const BORDER = "1px solid #E2E8F0";
const HDR_BG = "linear-gradient(#EDF4F5,#EDF4F5)";

export const DaywiseSheetTable: React.FC<Props> = ({ date, fetchFn, onDataLoaded }) => {
  const [data, setData] = useState<DaywiseData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    fetchFn({ date })
      .then((d) => { setData(d); onDataLoaded?.(d); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, fetchFn]);

  if (loading) return <SheetTableSkeleton cols={3} />;

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="flex-1 min-h-0 relative bg-white rounded-xl" style={{ border: BORDER, minHeight: 200 }}>
        <EmptyState />
      </div>
    );
  }

  const { dates, data: rows } = data;

  const stickyTsHeader: React.CSSProperties = {
    position: "sticky", top: 0, left: 0, zIndex: 22,
    backgroundImage: HDR_BG, color: TEAL, fontWeight: 700, fontSize: 12,
    height: 44, minWidth: 130, whiteSpace: "nowrap",
    border: BORDER, borderRight: `2px solid ${TEAL}`,
    borderBottom: "2px solid #CAD5E2",
    boxShadow: "3px 0 6px rgba(52,101,109,0.12)",
    textAlign: "center", verticalAlign: "middle",
  };

  const dateHeader: React.CSSProperties = {
    position: "sticky", top: 0, zIndex: 10,
    backgroundImage: HDR_BG, color: TEAL, fontWeight: 600, fontSize: 12,
    height: 44, minWidth: 120,
    border: BORDER, borderBottom: "2px solid #CAD5E2",
    textAlign: "center", verticalAlign: "middle", padding: "0 10px",
  };

  const stickyTd = (even: boolean): React.CSSProperties => ({
    position: "sticky", left: 0, zIndex: 5,
    backgroundColor: even ? "#FFFFFF" : "#F7FAFB",
    color: TEAL, fontWeight: 500, fontSize: 12,
    height: 34, minWidth: 130, whiteSpace: "nowrap",
    border: BORDER, borderRight: `2px solid ${TEAL}`,
    boxShadow: "3px 0 6px rgba(52,101,109,0.08)",
    textAlign: "center", verticalAlign: "middle",
  });

  const cell: React.CSSProperties = {
    border: BORDER, textAlign: "center", verticalAlign: "middle",
    fontSize: 12, padding: "0 10px", minWidth: 120, height: 34,
  };

  return (
    <div
      className="flex-1 min-h-0 rounded-xl overflow-hidden bg-white flex flex-col"
      style={{ border: BORDER, boxShadow: "0 2px 12px rgba(52,101,109,0.08)" }}
    >
      <div style={{ overflowX: "auto", overflowY: "auto", flex: 1, minHeight: 0 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, minWidth: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr>
              <th style={stickyTsHeader}>Time Slot</th>
              {dates.map((d) => (
                <th key={d} style={dateHeader}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const even = idx % 2 === 0;
              return (
                <tr key={row.time_block} style={{ backgroundColor: even ? "#FFFFFF" : "#F7FAFB" }}>
                  <td style={stickyTd(even)}>{row.time_slot}</td>
                  {row.values.map((v, i) => (
                    <td key={i} style={{ ...cell, color: v === null ? "#D4D4D8" : "#374151" }}>
                      {v === null ? "–" : v === 0 ? "0" : v.toFixed(2)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
